import React, { useState, useEffect, useRef } from 'react';
import Lottie from 'react-lottie-player';
import './OmicAtlas.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faUsers, faChartLine, faSignOutAlt, faBell, faUserPlus, faBars, faDna, faSoap, faHandsBubbles, faHouseFloodWater, faWater, faMicroscope, faFilePdf, faCloud, faCloudUpload, faFilePowerpoint, faFile, faFileAlt, faVialCircleCheck, faGear, faVials, faVial, faComments, faCircleNodes, faExplosion, faMagic, faCircleH, faGlobe, faRing, faLifeRing, faCircleNotch, faArrowAltCircleUp, faArrowUp, faUpload, faPlus, faStethoscope, faExpand, faCompress, faCheck, faHistory, faComment, faTimes, faCircle } from '@fortawesome/free-solid-svg-icons';
import ChatInput from './ChatInput';
import OmicsFrame from './OmicsFrame';
import ReactMarkdown from 'react-markdown';

const suggestions = [
    "Cohort Expression",
    "Cohort Selection",
    "Data Analysis",
    "Visualization Options",
];

// Change the API base URL to match your backend
const API_BASE_URL = 'https://service4.szapfs.org';

// Add a simple Markdown parser function
const parseMarkdown = (text) => {
    if (!text) return '';
    
    // Parse bold text (**text**)
    let formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Parse headers (### text)
    formattedText = formattedText.replace(/### (.*?)(?:\n|$)/g, '<h3>$1</h3>');
    
    // Parse numbered lists
    const listPattern = /(\d+\.\s+.*?)(?=\n\d+\.|$)/gs;
    const listMatches = formattedText.match(listPattern);
    
    if (listMatches) {
        let listHtml = '<ol>';
        listMatches.forEach(item => {
            const listItemText = item.replace(/\d+\.\s+/, '');
            listHtml += `<li>${listItemText}</li>`;
        });
        listHtml += '</ol>';
        
        // Replace the list in the original text
        for (const match of listMatches) {
            formattedText = formattedText.replace(match, '');
        }
        
        // Find where the list should be inserted
        const listMarker = formattedText.match(/\d+\.\s+/);
        if (listMarker) {
            const listPosition = formattedText.indexOf(listMarker[0]);
            formattedText = formattedText.substring(0, listPosition) + listHtml + formattedText.substring(listPosition + listMarker[0].length);
        } else {
            // Fallback - append the list at the end
            formattedText += listHtml;
        }
    }
    
    // Wrap paragraphs
    formattedText = formattedText.split('\n\n').map(para => `<p>${para}</p>`).join('');
    
    return formattedText;
};

const OmicAtlas = ({ user }) => {
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [showConversations, setShowConversations] = useState(false);
    const [omicsFrameMode, setOmicsFrameMode] = useState(false);
    const [omicsFrameData, setOmicsFrameData] = useState({
        cohort: '',
        analysis_type: '',
        omics_source: '',
        omics_frame_id: null
    });
    const [lottieData, setLottieData] = useState(null);
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);

    // Load Lottie animation on component mount
    useEffect(() => {
        fetch('https://lottie.host/e225fcd9-4a02-4001-a8f5-b67e21e1bc42/nAt6uKG3C5.json')
            .then(response => response.json())
            .then(data => setLottieData(data))
            .catch(error => console.error('Error loading Lottie animation:', error));
    }, []);

    // Load user conversations on component mount
    useEffect(() => {
        if (user?.id) {
            loadUserConversations();
        }
    }, [user]);

    // Add useEffect hook to handle class updates for OmicsFrame mode
    useEffect(() => {
        const container = document.querySelector('.omic-atlas-container');
        const frame = document.querySelector('.omic-atlas-frame');
        
        if (container && frame) {
            if (omicsFrameMode) {
                container.classList.add('frame-active');
                frame.classList.add('frame-active');
                console.log('Added frame-active classes');
            } else {
                container.classList.remove('frame-active');
                frame.classList.remove('frame-active');
                console.log('Removed frame-active classes');
            }
        }
    }, [omicsFrameMode]);

    const loadUserConversations = async () => {
        try {
            console.log('Loading conversations for user:', user.id);
            const response = await fetch(`${API_BASE_URL}/conversations?user_id=${user.id}`);
            
            if (!response.ok) {
                throw new Error('Failed to load conversations');
            }
            
            const data = await response.json();
            console.log('Loaded conversations:', data);
            setConversations(data);
        } catch (error) {
            console.error('Error loading conversations:', error);
        }
    };

    const loadConversation = async (conversationId) => {
        try {
            console.log('Loading conversation:', conversationId);
            const response = await fetch(`${API_BASE_URL}/conversation/${conversationId}`);
            
            if (!response.ok) {
                console.error('Failed to load conversation. Status:', response.status);
                throw new Error('Failed to load conversation');
            }
            
            const data = await response.json();
            console.log('Loaded conversation data:', data);
            
            if (data.messages && data.messages.length > 0) {
                // Process messages for display
                const processedMessages = [];
                
                // Filter out tool_result messages when loading a conversation
                const filteredMessages = data.messages.filter(msg => msg.type !== 'tool_result');
                
                let hasOmicsFrameTool = false;
                
                filteredMessages.forEach(msg => {
                    const message = {
                        id: msg.id,
                        text: msg.content,
                        sender: msg.role === 'user' ? 'user' : 'bot',
                        timestamp: msg.created_at || new Date().toISOString(),
                        type: msg.type || 'text',
                        activeTools: []
                    };
                    
                    // Add tool information if available
                    if (msg.tool) {
                        message.tool = {
                            name: msg.tool.name,
                            status: msg.tool.status || 'completed',
                            input: msg.tool.input,
                            output: msg.tool.output,
                            omics_frame_id: msg.tool.omics_frame_id
                        };
                        
                        // Add the tool to activeTools array as well to ensure it renders properly
                        message.activeTools = [{
                            name: msg.tool.name,
                            status: msg.tool.status || 'completed'
                        }];
                        
                        // Check if this is an OmicsFrame tool
                        if (msg.tool.name === 'OmicsFrame') {
                            hasOmicsFrameTool = true;
                        }
                    }
                    
                    processedMessages.push(message);
                });
                
                setMessages(processedMessages);
                
                // Force scroll after the state update has been processed
                setTimeout(() => {
                    const chatContainer = document.querySelector('.omic-chat-messages');
                    if (chatContainer) {
                        chatContainer.scrollTop = chatContainer.scrollHeight;
                        console.log('Forced scroll after loading conversation, height:', chatContainer.scrollHeight);
                    }
                }, 100);
                
                // Check if there are any OmicsFrame tools in the conversation
                // and load the most recent one's data
                if (hasOmicsFrameTool) {
                    try {
                        const omicsFramesResponse = await fetch(`${API_BASE_URL}/conversation/${conversationId}/omicsframes`);
                        const omicsFramesData = await omicsFramesResponse.json();
                        
                        if (omicsFramesData.omicsframes && omicsFramesData.omicsframes.length > 0) {
                            // Get the most recent OmicsFrame
                            const latestFrame = omicsFramesData.omicsframes[0];
                            console.log('Found existing OmicsFrame data:', latestFrame);
                            
                            // Set the OmicsFrame data but don't activate it automatically
                            setOmicsFrameData({
                                cohort: latestFrame.cohort,
                                analysis_type: latestFrame.analysis_type,
                                omics_source: latestFrame.omics_source,
                                omics_frame_id: latestFrame.id
                            });
                        }
                    } catch (error) {
                        console.error('Error loading OmicsFrames for conversation:', error);
                    }
                }
            }
            
            setCurrentConversationId(conversationId);
            setShowConversations(false);
        } catch (error) {
            console.error('Error loading conversation:', error);
        }
    };

    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return;

        const newMessage = {
            text: inputMessage,
            sender: 'user',
            timestamp: new Date().toISOString(),
            type: 'text'
        };

        setMessages(prev => [...prev, newMessage]);
        setInputMessage('');
        setIsLoading(true);

        // Add a temporary pulsing circle as an assistant message
        const temporaryBotMessage = {
            text: '',
            sender: 'bot',
            timestamp: new Date().toISOString(),
            type: 'text',
            isTemporary: true,
            tempIndicator: true
        };
        
        setMessages(prev => [...prev, temporaryBotMessage]);

        try {
            const response = await fetch(`${API_BASE_URL}/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream',
                },
                body: JSON.stringify({
                    user_id: user.id,
                    conversation_id: currentConversationId,
                    message: inputMessage
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            let botMessageAdded = false;
            let currentTool = null;
            let accumulatedResponse = '';

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            while (true) {
                const { value, done } = await reader.read();
                
                if (done) {
                    console.log('Stream complete');
                    break;
                }

                const chunkText = decoder.decode(value);
                const lines = chunkText.split('\n');

                // Add a bot message placeholder that will be updated with the response content
                if (!botMessageAdded) {
                    // Remove the temporary message and add the actual bot message
                    setMessages(prev => {
                        const filteredMessages = prev.filter(msg => !msg.isTemporary);
                        return [...filteredMessages, {
                            text: '', // Empty text initially
                            sender: 'bot',
                            timestamp: new Date().toISOString(),
                            type: 'text',
                            activeTools: []
                        }];
                    });
                    botMessageAdded = true;
                }

                for (const line of lines) {
                    const trimmedLine = line.trim();
                    if (!trimmedLine || !trimmedLine.startsWith('data: ')) continue;

                    try {
                        const jsonString = trimmedLine.slice(5).trim();
                        if (!jsonString) continue;
                        
                        const jsonData = JSON.parse(jsonString);
                        console.log('Parsed data:', jsonData);

                        // Handle conversation ID from initial metadata
                        if (jsonData.conversation_id && !currentConversationId) {
                            setCurrentConversationId(jsonData.conversation_id);
                            continue;
                        }
                        
                        // Handle different message types
                        if (jsonData.type === 'tool_call') {
                            // When tool_call is received, create a new message for the tool instead of updating the last one
                            console.log('Tool call received:', jsonData.tool);
                            
                            const toolMessage = {
                                text: '',
                                sender: 'bot',
                                timestamp: new Date().toISOString(),
                                type: 'tool',
                                activeTools: [
                                    {
                                        name: jsonData.tool.name,
                                        status: 'using'
                                    }
                                ],
                                tool: {
                                    name: jsonData.tool.name,
                                    status: 'using'
                                }
                            };
                            
                            // Add a new message for the tool
                            setMessages(prev => [...prev, toolMessage]);
                            currentTool = jsonData.tool.name;
                            
                        } else if (jsonData.type === 'tool_result') {
                            // When tool_result is received, update the tool message with the result
                            console.log('Tool result received:', jsonData.result);
                            console.log('Tool result tool data:', jsonData.tool);
                            
                            // Auto-activate OmicsFrame mode if this is an OmicsFrame tool
                            if (jsonData.tool && jsonData.tool.name === 'OmicsFrame') {
                                try {
                                    const output = jsonData.result || '';
                                    const cohortMatch = output.match(/cohort: ([^,]+)/);
                                    const analysisMatch = output.match(/analysis: ([^,]+)/);
                                    const sourceMatch = output.match(/source: ([^,]+)/);
                                    
                                    const cohort = cohortMatch ? cohortMatch[1] : '';
                                    const analysis_type = analysisMatch ? analysisMatch[1] : '';
                                    const omics_source = sourceMatch ? sourceMatch[1] : '';
                                    
                                    // Set OmicsFrame data and activate mode immediately
                                    setOmicsFrameData({
                                        cohort,
                                        analysis_type,
                                        omics_source,
                                        omics_frame_id: jsonData.tool.omics_frame_id || null
                                    });
                                    setOmicsFrameMode(true);
                                } catch (error) {
                                    console.error('Error parsing OmicsFrame data:', error);
                                }
                            }
                            
                            // Update the tool message with the result
                            setMessages(prev => {
                                const newMessages = [...prev];
                                
                                // Find the tool message to update
                                const toolMessageIndex = newMessages.findIndex(msg => 
                                    msg.type === 'tool' && 
                                    msg.tool && 
                                    msg.tool.name === jsonData.tool.name && 
                                    msg.tool.status === 'using'
                                );
                                
                                if (toolMessageIndex !== -1) {
                                    // Update the tool message
                                    newMessages[toolMessageIndex] = {
                                        ...newMessages[toolMessageIndex],
                                        activeTools: [
                                            {
                                                name: jsonData.tool.name,
                                                status: 'completed'
                                            }
                                        ],
                                        tool: {
                                            name: jsonData.tool.name,
                                            status: 'completed',
                                            input: jsonData.tool.input,
                                            output: jsonData.result,
                                            omics_frame_id: jsonData.tool.omics_frame_id || null
                                        }
                                    };
                                }
                                
                                return newMessages;
                            });
                            
                            // Reset current tool
                            currentTool = null;
                            
                            // Start a new bot message for text that comes after the tool result
                            const newBotMessage = {
                                text: '',
                                sender: 'bot',
                                timestamp: new Date().toISOString(),
                                type: 'text',
                                activeTools: []
                            };
                            
                            setMessages(prev => [...prev, newBotMessage]);
                            
                            // Reset accumulated response for the new message
                            accumulatedResponse = '';
                        } else if (jsonData.type === 'final_response') {
                            // Final response after tool execution
                            if (jsonData.content) {
                                accumulatedResponse += jsonData.content;
                                setMessages(prev => {
                                    const newMessages = [...prev];
                                    const lastMessage = newMessages[newMessages.length - 1];
                                    
                                    // Only update if the last message is a text message (not a tool message)
                                    if (lastMessage.type === 'text') {
                                        newMessages[newMessages.length - 1] = {
                                            ...lastMessage,
                                            text: accumulatedResponse
                                        };
                                    }
                                    
                                    return newMessages;
                                });
                            }
                        } else if (jsonData.type === 'done') {
                            // Stream is finished
                            console.log('Stream is done');
                            // Refresh conversations list
                            loadUserConversations();
                        } else if (jsonData.content) {
                            // Regular content update
                            accumulatedResponse += jsonData.content;
                            setMessages(prev => {
                                const newMessages = [...prev];
                                const lastMessage = newMessages[newMessages.length - 1];
                                
                                // Only update if the last message is a text message (not a tool message)
                                if (lastMessage.type === 'text') {
                                    newMessages[newMessages.length - 1] = {
                                        ...lastMessage,
                                        text: accumulatedResponse
                                    };
                                }
                                
                                return newMessages;
                            });
                        }

                    } catch (e) {
                        console.error('Error processing line:', e, 'Line:', trimmedLine);
                    }
                }
            }

        } catch (error) {
            console.error('Error communicating with the backend:', error);
            setMessages(prev => [...prev, {
                text: 'Sorry, there was an error communicating with the service. Please try again later.',
                sender: 'bot',
                timestamp: new Date().toISOString(),
                type: 'text'
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleToolClick = async (message) => {
        if (message.tool && message.tool.name === 'OmicsFrame') {
            try {
                console.log('OmicsFrame tool clicked, processing...', message);
                console.log('Tool data:', message.tool);
                
                // Try to fetch the omicsframe ID if it's not already in the message
                let frameId = message.tool.omics_frame_id;
                
                if (!frameId && currentConversationId) {
                    try {
                        // Fetch all omicsframes for this conversation
                        const response = await fetch(`${API_BASE_URL}/conversation/${currentConversationId}/omicsframes`);
                        const data = await response.json();
                        
                        console.log('Fetched omicsframes for conversation:', data);
                        
                        if (data.omicsframes && data.omicsframes.length > 0) {
                            // Find a matching frame based on name or just use the most recent one
                            const toolInput = JSON.parse(message.tool.input || '{}');
                            const frameName = toolInput.name;
                            
                            let matchingFrame = null;
                            
                            if (frameName) {
                                matchingFrame = data.omicsframes.find(frame => 
                                    frame.name === frameName);
                            }
                            
                            // If no matching frame found by name, use the most recent one
                            if (!matchingFrame && data.omicsframes.length > 0) {
                                matchingFrame = data.omicsframes[0];
                            }
                            
                            if (matchingFrame) {
                                console.log('Found matching frame:', matchingFrame);
                                frameId = matchingFrame.id;
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching omicsframes:', error);
                    }
                }
                
                // Parse the tool output to extract parameters
                const output = message.tool.output || '';
                const cohortMatch = output.match(/cohort: ([^,]+)/);
                const analysisMatch = output.match(/analysis: ([^,]+)/);
                const sourceMatch = output.match(/source: ([^,]+)/);
                
                const cohort = cohortMatch ? cohortMatch[1] : '';
                const analysis_type = analysisMatch ? analysisMatch[1] : '';
                const omics_source = sourceMatch ? sourceMatch[1] : '';
                
                // Try to extract more info from the input if available
                let toolInput = {};
                try {
                    toolInput = JSON.parse(message.tool.input || '{}');
                } catch (e) {
                    console.error('Error parsing tool input:', e);
                }
                
                // Set OmicsFrame data
                setOmicsFrameData({
                    cohort,
                    analysis_type,
                    omics_source,
                    omics_frame_id: frameId
                });
                
                // Log the frame ID being used
                console.log('Setting omicsFrameMode to true with frame ID:', frameId);
                
                // Don't activate if we couldn't find a valid frame ID
                if (!frameId) {
                    console.error('No valid frame ID found, cannot open analysis');
                    // Display an error to the user
                    setMessages(prevMessages => [...prevMessages, {
                        id: Date.now(),
                        text: "Cannot open analysis: missing frame ID. Please try creating a new analysis.",
                        sender: 'bot',
                        timestamp: new Date().toISOString(),
                        type: 'error'
                    }]);
                    return;
                }
                
                // Activate OmicsFrame mode - this should trigger the CSS class change
                setOmicsFrameMode(true);
            } catch (error) {
                console.error('Error parsing OmicsFrame data:', error);
            }
        }
    };

    const closeOmicsFrameMode = () => {
        setOmicsFrameMode(false);
    };

    const renderMessage = (message) => {
        return (
            <div key={message.id || message.key} className={`omic-message ${message.sender}-message`}>
                <div className="omic-message-content">
                    {message.tempIndicator ? (
                        <div className="temp-assistant-indicator">
                            <FontAwesomeIcon icon={faCircle} className="pulsing-circle" spin />
                        </div>
                    ) : message.sender === 'bot' ? (
<ReactMarkdown>{message.text}</ReactMarkdown>





) : (
                        message.text
                    )}
                    {message.activeTools && message.activeTools.map((tool, index) => (
                        <div key={`tool-${index}`} className="omic-tool-indicator">
                            <div 
                                className={`tool-status ${tool.status} ${tool.name === 'OmicsFrame' && tool.status === 'completed' ? 'clickable' : ''}`}
                                onClick={(e) => {
                                    if (tool.name === 'OmicsFrame' && tool.status === 'completed') {
                                        e.stopPropagation();
                                        handleToolClick(message);
                                    }
                                }}
                            >
                                {tool.status === 'using' ? (
                                    <>
                                        <FontAwesomeIcon icon={faGear} className="spin-icon" />
                                        <span>Activating {tool.name}...</span>
                                    </>
                                ) : tool.status === 'completed' ? (
                                    <>
                                        <FontAwesomeIcon icon={faFile} className="success-icon" />
                                        {tool.name}
                                    </>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>

            </div>
        );
    };

    const renderConversationsList = () => (
        <div className="omic-conversations-list">
            <div className="omic-conversations-header">
                <h3>Conversations</h3>
                <button onClick={() => {
                    console.log('Starting new conversation');
                    setCurrentConversationId(null);
                    setMessages([]);
                    setShowConversations(false);
                }} className="new-chat-button">
                    <FontAwesomeIcon icon={faPlus} /> New Chat
                </button>
            </div>
            {conversations.map((conv) => (
                <div
                    key={conv.id}
                    className={`conversation-item ${conv.id === currentConversationId ? 'active' : ''}`}
                    onClick={() => {
                        console.log('Selected conversation:', conv);
                        loadConversation(conv.id);
                    }}
                >
                    <FontAwesomeIcon icon={faComment} />
                    <div className="conversation-info">
                        <div className="conversation-preview">{conv.preview || 'New conversation'}</div>
                        <div className="conversation-date">{new Date(conv.created_at).toLocaleDateString()}</div>
                    </div>
                </div>
            ))}
        </div>
    );

    useEffect(() => {
        const scrollChatToBottom = () => {
            const chatContainer = document.querySelector('.omic-chat-messages');
            if (chatContainer) {
                chatContainer.scrollTop = chatContainer.scrollHeight;
                console.log('Scrolled to bottom, height:', chatContainer.scrollHeight);
            }
        };

        // Scroll when messages change
        if (messages.length > 0) {
            // Try multiple times to ensure it catches after any renders
            scrollChatToBottom();
            setTimeout(scrollChatToBottom, 50);
            setTimeout(scrollChatToBottom, 150);
            setTimeout(scrollChatToBottom, 300);
        }
    }, [messages]);

    return (
        <div className="omic-atlas-container-parent">
            <div className={`omic-atlas-container ${omicsFrameMode ? 'frame-active' : ''}`}>
                <div className="omic-atlas-container-top">
                    <div className="omic-atlas-history-toggle" onClick={() => setShowConversations(!showConversations)}>
                        <FontAwesomeIcon icon={faHistory} />
                    </div>

                </div>
                
                <div className={`omic-atlas-container-middle ${messages.length === 0 ? 'empty' : ''}`}>
                    {showConversations ? (
                        renderConversationsList()
                    ) : messages.length === 0 ? (
                        <div className="omic-atlas-placeholder">
                            <Lottie
                                loop
                                animationData={lottieData}
                                play
                                className="omic-atlas-placeholder-lottie"
                            />
                            <h2 className="omic-atlas-intro-text">What can I help you with?</h2>
                            <div className="omic-atlas-placeholderchat">
                                <ChatInput 
                                    onSendMessage={handleSendMessage}
                                    inputMessage={inputMessage}
                                    setInputMessage={setInputMessage}
                                    isLoading={isLoading}
                                />
                            </div>
                            <div className="omic-atlas-suggestions">
                                {suggestions.map((suggestion, index) => (
                                    <div 
                                        key={index} 
                                        className="omic-atlas-suggestion"
                                        onClick={() => {
                                            setInputMessage(suggestion);
                                            handleSendMessage();
                                        }}
                                    >
                                        {suggestion}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="omic-chat-messages" ref={chatContainerRef}>
                            {[...messages].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)).map((message, index) => (
                                renderMessage({ ...message, key: index })
                            ))}
                            {isLoading && (
                                <div className="bot-message loading">
                                    
                                </div>
                            )}
                            <div id="chat-end" ref={messagesEndRef} style={{ height: "30px", width: "100%", clear: "both" }} />
                        </div>
                    )}
                </div>

                {messages.length > 0 && !showConversations && (
                    <div className="omic-atlas-container-bottom">
                        <ChatInput 
                            onSendMessage={handleSendMessage}
                            inputMessage={inputMessage}
                            setInputMessage={setInputMessage}
                            isLoading={isLoading}
                        />
                    </div>
                )}
            </div>

            <div className={`omic-atlas-frame ${omicsFrameMode ? 'frame-active' : ''}`}>
                {omicsFrameMode && (
                    <OmicsFrame 
                        omicsFrameData={omicsFrameData}
                        onClose={closeOmicsFrameMode}
                        conversationId={currentConversationId}
                    />
                )}
            </div>
        </div>
    );
};

export default OmicAtlas;